// form
import { useFormContext, Controller } from 'react-hook-form';
// @mui
import { TextField, TextFieldProps } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker, DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
// ----------------------------------------------------------------------

type Props = TextFieldProps & {
  name: string;
  label: string;
  status?: boolean;
  className?: string;
  minDate?: Date | Dayjs | null;
  withTime?: boolean;
};

export default function RHFDatePicker({
  name,
  label,
  status,
  value,
  className,
  minDate,
  withTime = false,
  disabled,
  helperText,
  ...other
}: Props) {
  const { control } = useFormContext();
  const fieldDateValue = fieldValueToDayjs(value);
  const PickerComponent = withTime ? DateTimePicker : DatePicker;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <PickerComponent
            disabled={disabled ?? status}
            label={label}
            value={field.value ? fieldValueToDayjs(field.value) : fieldDateValue}
            onChange={(newValue) => {
              if (!newValue) {
                field.onChange(null);
                return;
              }

              field.onChange(newValue.isValid() ? newValue.toDate() : null);
            }}
            minDate={minDate ? dayjs(minDate) : undefined}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                error={!!error}
                helperText={error?.message || helperText}
                className={className}
                sx={{
                  '& .MuiInputBase-root.Mui-disabled': {
                    backgroundColor: '#f5f5f5',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e0e0e0',
                    },
                  },
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: '#9e9e9e',
                    cursor: 'not-allowed',
                  },
                  '& .MuiInputLabel-root.Mui-disabled': {
                    color: '#9e9e9e',
                  },
                  ...other.sx,
                }}
                {...other}
              />
            )}
          />
        </LocalizationProvider>
      )}
    />
  );
}

function fieldValueToDayjs(input?: unknown): Dayjs | null {
  if (!input) {
    return null;
  }

  const parsed = dayjs(input as string | Date);
  return parsed.isValid() ? parsed : null;
}
